import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Trash2, AlertTriangle, Download } from 'lucide-react';
import { CSVImporter } from './CSVImporter';
import { dateSplitter } from '@/utils/formatting/deteSplitter';

interface DataManagementCardProps {
  settings: any;
}

export const DataManagementCard = ({ settings }: DataManagementCardProps) => {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const handleDeleteAcademicYearData = async () => {
    if (confirmText !== 'حذف البيانات') {
      toast({
        title: "خطأ في التأكيد",
        description: "يرجى كتابة 'حذف البيانات' بالضبط للتأكيد",
        variant: "destructive"
      });
      return;
    }

    setIsDeleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('غير مسموح');

      // Get current school ID
      const schoolId = user.id;

      // Delete attendance records
      const { error: attendanceError } = await supabase
        .from('attendance_records')
        .delete()
        .eq('school_id', schoolId);

      if (attendanceError) throw attendanceError;

      // Delete late attendance records
      const { error: lateAttendanceError } = await supabase
        .from('late_attendance_records')
        .delete()
        .eq('school_id', schoolId);

      if (lateAttendanceError) throw lateAttendanceError;
      
      toast({
        title: "تم الحذف بنجاح",
        description: "تم حذف جميع بيانات العام الدراسي (الحضور والتأخر) بنجاح",
        variant: "default"
      });

      setConfirmText('');
    } catch (error) {
      console.error('Error deleting academic year data:', error);
      toast({
        title: "خطأ في الحذف",
        description: "حدث خطأ أثناء حذف البيانات. يرجى المحاولة مرة أخرى",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('غير مسموح');

      const schoolId = user.id;

      // Get all attendance data
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('attendance_records')
        .select(`
          *,
          students!attendance_records_student_id_fkey(name, student_id, grade, section)
        `)
        .eq('school_id', schoolId)
        .order('date', { ascending: false });

      if (attendanceError) throw attendanceError;

      // Get all late attendance data with student information
      const { data: lateAttendanceData, error: lateError } = await supabase
        .from('late_attendance_records')
        .select('*')
        .eq('school_id', schoolId)
        .order('date', { ascending: false });

      if (lateError) throw lateError;

      // Get student information for late attendance records
      const studentIds = lateAttendanceData?.map(record => record.student_id) || [];
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('id, name, student_id, grade, section')
        .in('id', studentIds);

      if (studentsError) throw studentsError;

      // Convert attendance data to CSV format
      const csvHeaders = 'النوع,التاريخ,اسم الطالب,رقم الطالب,الصف,الشعبة,الحالة,الحصة,نوع التأخر,ملاحظات\n';
      
      // Regular attendance data
      const attendanceCsvData = attendanceData.map(record => {
        return [
          'حضور',
          record.date,
          record.students.name,
          record.students.student_id,
          record.students.grade,
          record.students.section,
          record.status === 'present' ? 'حاضر' : record.status === 'absent'  || record.status === 'absent_with_reason' ? 'غائب' : 'متأخر',
          record.period || '',
          '',
          record.notes || ''
        ].join(',');
      });

      // Late attendance data
      const lateAttendanceCsvData = lateAttendanceData?.map(record => {
        const student = studentsData?.find(s => s.id === record.student_id);
        if (!student) return null; // Skip if student not found
        
        const lateTypeMap = {
          'morning_lineup': 'تأخر طابور الصباح',
          'after_lineup': 'تأخر بعد الطابور',
          'period_1': 'تأخر الحصة الأولى',
          'period_2': 'تأخر الحصة الثانية',
          'period_3': 'تأخر الحصة الثالثة',
          'period_4': 'تأخر الحصة الرابعة',
          'period_5': 'تأخر الحصة الخامسة',
          'period_6': 'تأخر الحصة السادسة',
          'period_7': 'تأخر الحصة السابعة'
        };
        
        return [
          'تأخر',
          record.date,
          student.name,
          student.student_id,
          student.grade,
          student.section,
          'متأخر',
          record.period || '',
          lateTypeMap[record.late_type] || record.late_type,
          record.notes || ''
        ].join(',');
      }).filter(Boolean) || []; // Remove null entries

      const allCsvData = [...attendanceCsvData, ...lateAttendanceCsvData];
      const csvContent = csvHeaders + allCsvData.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `بيانات_الحضور_${dateSplitter(new Date())}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "تم التصدير بنجاح",
        description: "تم تصدير بيانات الحضور بنجاح",
        variant: "default"
      });

    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "خطأ في التصدير",
        description: "حدث خطأ أثناء تصدير البيانات",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getCurrentAcademicYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // إذا كان الشهر الحالي قبل سبتمبر، فالسنة الدراسية الحالية تبدأ من العام السابق
    if (currentMonth < 8) { // 8 = سبتمبر (0-indexed)
      return `${currentYear - 1}-${currentYear}`;
    } else {
      return `${currentYear}-${currentYear + 1}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Data Card */}
      <CSVImporter settings={settings} />
      
      {/* Export Data Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-700">
            <Download className="w-5 h-5" />
            تصدير البيانات
          </CardTitle>
          <CardDescription>
            قم بتصدير جميع بيانات الحضور والتأخر للعام الدراسي الحالي ({getCurrentAcademicYear()})
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-800 mb-2">
                سيتم تصدير البيانات التالية:
              </p>
              <ul className="text-sm text-green-700 space-y-1 mr-4">
                <li>• جميع سجلات الحضور</li>
                <li>• جميع سجلات التأخر</li>
                <li>• أسماء الطلاب ومعلوماتهم</li>
                <li>• التواريخ والحصص</li>
              </ul>
            </div>
            
            <Button 
              onClick={handleExportData}
              disabled={isExporting}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isExporting ? 'جاري التصدير...' : 'تصدير البيانات (CSV)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Data Card */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            المنطقة الخطرة
          </CardTitle>
          <CardDescription>
            حذف جميع بيانات العام الدراسي - هذا الإجراء لا يمكن التراجع عنه
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-red-800 mb-2">
                تحذير: سيتم حذف البيانات التالية نهائياً:
              </p>
              <ul className="text-sm text-red-700 space-y-1 mr-4">
                <li>• جميع سجلات الحضور للعام الدراسي</li>
                <li>• جميع سجلات التأخر</li>
                <li>• التقارير والإحصائيات المرتبطة</li>
              </ul>
              <p className="text-sm text-red-800 mt-2 font-semibold">
                ملاحظة: لن يتم حذف بيانات الطلاب أو إعدادات المدرسة
              </p>
            </div>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full">
                  <Trash2 className="w-4 h-4 ml-2" />
                  حذف بيانات العام الدراسي
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-red-700">
                    تأكيد حذف بيانات العام الدراسي
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-right">
                    هذا الإجراء سيحذف جميع بيانات الحضور والتأخر للعام الدراسي الحالي ({getCurrentAcademicYear()}) نهائياً.
                    <br /><br />
                    لن يتم حذف بيانات الطلاب أو إعدادات المدرسة.
                    <br /><br />
                    للتأكيد، اكتب: <strong>حذف البيانات</strong>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                
                <div className="my-4">
                  <Label htmlFor="confirm-text">كلمة التأكيد:</Label>
                  <Input
                    id="confirm-text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="حذف البيانات"
                    className="mt-2"
                    dir="rtl"
                  />
                </div>

                <AlertDialogFooter className="flex justify-start gap-2">
                  <AlertDialogCancel>إلغاء</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteAcademicYearData}
                    disabled={isDeleting || confirmText !== 'حذف البيانات'}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {isDeleting ? 'جاري الحذف...' : 'تأكيد الحذف'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};